import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Header } from "../components/Header";
import { Seo } from "../components/Seo";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { toast } from "sonner";
import { UtensilsCrossed } from "lucide-react";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Une erreur est survenue.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function CustomerLogin() {
  const navigate = useNavigate();
  const { setCustomer } = useAuth();
  const { t } = useLang();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const endpoint = mode === "register" ? "/auth/register" : "/auth/login";
      const body = mode === "register"
        ? { name: form.name, email: form.email, password: form.password }
        : { email: form.email, password: form.password };
      const res = await api.post(endpoint, body);
      setCustomer(res.data);
      toast.success(mode === "register" ? "Compte créé !" : "Connexion réussie !");
      const role = res.data?.role;
      navigate(role === "super_admin" || role === "staff" ? "/admin" : "/");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full h-12 px-4 rounded-xl bg-[#FDFBF7] border border-[#E5E0D8] outline-none focus:border-[#E63946] transition-colors";

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Seo
        title="Connexion — Vytali"
        description="Connectez-vous à votre espace membre Vytali."
        path="/login"
        noIndex
      />
      <Header />
      <div className="max-w-md mx-auto px-6 py-16">
        <div className="bg-white border border-[#E5E0D8] rounded-2xl p-8">
          <span className="w-12 h-12 rounded-full bg-[#E63946] flex items-center justify-center mb-6">
            <UtensilsCrossed className="w-6 h-6 text-white" />
          </span>
          <h1 className="text-3xl font-display font-black tracking-tighter text-[#2B2B2B]">
            {mode === "login" ? t("clogin.welcome") : t("clogin.createAccount")}
          </h1>
          <p className="mt-2 text-[#5C5C5C]">
            {mode === "login" ? t("clogin.subtitleLogin") : t("clogin.subtitleRegister")}
          </p>

          <form onSubmit={submit} className="space-y-4 mt-8">
            {mode === "register" && (
              <input data-testid="customer-name-input" required value={form.name} onChange={set("name")} placeholder={t("clogin.fullName")} className={inputCls} />
            )}
            <input data-testid="customer-email-input" type="email" required value={form.email} onChange={set("email")} placeholder={t("clogin.email")} className={inputCls} />
            <input data-testid="customer-password-input" type="password" required value={form.password} onChange={set("password")} placeholder={t("clogin.password")} className={inputCls} />
            {mode === "login" && (
              <div className="text-right">
                <button
                  type="button"
                  data-testid="customer-forgot-password-link"
                  onClick={() => navigate("/forgot-password?flow=customer")}
                  className="text-sm font-medium text-[#E63946] hover:underline"
                >
                  {t("rauth.forgotPassword")}
                </button>
              </div>
            )}
            <button data-testid="customer-submit-btn" type="submit" disabled={busy}
              className="w-full h-12 rounded-full bg-[#E63946] text-white font-bold hover:scale-[1.02] transition-transform disabled:opacity-50">
              {busy ? "…" : mode === "login" ? t("clogin.submitLogin") : t("clogin.submitRegister")}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-[#5C5C5C]">
            {mode === "login" ? t("clogin.noAccount") : t("clogin.haveAccount")}{" "}
            <button data-testid="toggle-customer-mode" onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="font-bold text-[#E63946] hover:underline">
              {mode === "login" ? t("clogin.signup") : t("clogin.signin")}
            </button>
          </p>
          <p className="mt-3 text-center text-sm text-[#5C5C5C]">
            {t("clogin.restaurantPrompt")}{" "}
            <a data-testid="to-restaurant-auth" href="/restaurant" className="font-bold text-[#E63946] hover:underline">{t("header.restaurantSpace")}</a>
          </p>
        </div>
      </div>
    </div>
  );
}
